import {
  faArrowUpRightFromSquare,
  faBoxArchive,
  faCalendar,
  faExclamationCircle,
  faLock,
  faUser,
  faXmark,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";

import { DisplayDateTime } from "src/base-components/Date";
import { Divider } from "src/base-components/Divider";
import {
  PropertiesList,
  PropertiesListItem,
} from "src/base-components/PropertiesList";
import {
  ResizeHandle,
  useResizableSidepane,
} from "src/base-components/ResizableSidepane";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { CaseStatusPillWithSchema } from "src/cases/common/CaseStatusPillWithSchema";
import {
  renderByType,
  useCaseEntityDisplayMap,
} from "src/cases/common/casePropertyRenderers";
import { UserPill } from "src/cases/common/UserPill";
import { useGetCasePropertyIcon } from "src/cases/common/useGetCasePropertyIcon";
import { useCase } from "src/cases/queries/cases";
import { RESERVED_PROPERTY_KEYS } from "src/cases/queues/utils";
import { CaseEnvironment } from "src/clients/case-management";
import { EmptyState } from "src/design-system/EmptyState";
import { IconButton } from "src/design-system/Icon";
import { Environment, useEnvironment } from "src/hooks/useEnvironment";
import {
  EntityViewParams,
  getBaseUrl,
  getUrlToCaseViewPage,
} from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type CaseViewPaneProps = {
  caseId: string;
  onClose: () => void;
};

type CaseErrorState = {
  headline: string;
  description: string;
  icon: IconDefinition;
};

const getErrorState = (status?: number): CaseErrorState => {
  switch (status) {
    case 403:
      return {
        headline: "Access restricted",
        description: "You don't have permission to view this Case",
        icon: faLock,
      };
    case 409:
      return {
        headline: "Case is outdated",
        description:
          "This case was created before breaking changes were made to the case template.",
        icon: faExclamationCircle,
      };
    case 410:
      return {
        headline: "Case archived",
        description: "This case has been archived.",
        icon: faBoxArchive,
      };
    default:
      return {
        headline: "Error loading Case",
        description:
          "We're having trouble loading this Case. Try refreshing the page",
        icon: faExclamationCircle,
      };
  }
};

export const CaseViewPane: React.FC<CaseViewPaneProps> = ({
  caseId,
  onClose,
}) => {
  const { width } = useResizableSidepane();
  const { orgId, wsId } = useParamsDecode<EntityViewParams>();
  const [environment] = useEnvironment();
  const getPropertyIcon = useGetCasePropertyIcon();

  const { data: caseData, isLoading, error } = useCase(caseId);

  const template = caseData?.data.template;
  const templateVersion = caseData?.data.template_version;
  const caseProperties = caseData?.data.case_properties;

  const caseEnv =
    environment === "live" ? CaseEnvironment.LIVE : CaseEnvironment.SANDBOX;

  const { displayMap: entityDisplayMap } = useCaseEntityDisplayMap({
    caseProperties,
    schemaProperties: templateVersion?.case_properties,
    environment: environment as Environment,
  });

  const caseUrl = getUrlToCaseViewPage(orgId, wsId, caseId);

  const renderBody = () => {
    if (isLoading) {
      return <PropertiesList isLoading items={[]} loadingRows={6} showIcons />;
    }

    if (error || !caseData || !templateVersion || !template) {
      const errorState = getErrorState(error?.response?.status);
      return (
        <EmptyState
          dataLoc="case-pane-error-state"
          description={errorState.description}
          headline={errorState.headline}
          icon={errorState.icon}
          size="compact"
          variant="error"
        />
      );
    }

    const schemaProperties = templateVersion.case_properties;
    const assigneeId = caseData.data.assignee?.id;

    const metadataItems: PropertiesListItem[] = [
      {
        key: "status",
        label: "Status",
        icon: getPropertyIcon(schemaProperties.status),
        value: (
          <CaseStatusPillWithSchema
            status={caseData.data.case_properties.status}
            subStatus={caseData.data.case_properties.sub_status}
            templateId={template.id}
            versionId={templateVersion.id}
          />
        ),
      },
      {
        key: "assignee",
        label: "Assigned to",
        icon: faUser,
        value: assigneeId ? (
          <UserPill userId={assigneeId} />
        ) : (
          <span className="font-inter-normal-12px text-gray-500">
            Unassigned
          </span>
        ),
      },
      {
        key: "created_at",
        label: "Created at",
        icon: faCalendar,
        value: (
          <span className="font-inter-normal-12px">
            <DisplayDateTime value={caseData.data.created_at} />
          </span>
        ),
      },
      {
        key: "updated_at",
        label: "Updated at",
        icon: faCalendar,
        value: (
          <span className="font-inter-normal-12px">
            <DisplayDateTime value={caseData.data.updated_at} />
          </span>
        ),
      },
    ];

    // Template-defined properties, excluding the reserved status/sub_status
    // fields already shown in the metadata group above.
    const templateItems: PropertiesListItem[] = Object.entries(
      caseProperties ?? {},
    )
      .filter(
        ([key]) =>
          schemaProperties[key] && !RESERVED_PROPERTY_KEYS.includes(key),
      )
      .map(([key, value]) => {
        const schema = schemaProperties[key];
        const icon = getPropertyIcon(schema);

        return {
          key,
          label: schema.display_name || key,
          icon,
          value: renderByType({
            value,
            schema,
            caseProperties: caseProperties ?? {},
            key,
            icon,
            caseEnv,
            entityDisplayMap,
          }),
        };
      });

    return (
      <div className="flex flex-col gap-y-4">
        <PropertiesList
          dataLocPrefix="case-pane-metadata"
          items={metadataItems}
          showIcons
        />
        {templateItems.length > 0 && (
          <>
            <Divider />
            <PropertiesList
              dataLocPrefix="case-pane-properties"
              items={templateItems}
              showIcons
            />
          </>
        )}
      </div>
    );
  };

  return (
    <div
      className="relative flex shrink-0 flex-col gap-y-4 border-gray-200 border-l bg-white p-5"
      data-loc="case-view-pane"
      style={{ width: `${width}px` }}
    >
      <ResizeHandle />
      <div className="flex items-center justify-between gap-x-2">
        <div className="flex min-w-0 items-center gap-x-2">
          <span className="font-inter-semibold-13px text-gray-800">Case</span>
          <TruncatedIdPill id={caseId} variant="last-five" />
        </div>
        <div className="flex items-center gap-x-1">
          <IconButton
            dataLoc="case-pane-open"
            icon={faArrowUpRightFromSquare}
            size="xs"
            aria-label="Open case in new tab"
            onClick={() => window.open(getBaseUrl() + caseUrl, "_blank")}
          />
          <IconButton
            dataLoc="case-pane-close"
            icon={faXmark}
            size="xs"
            aria-label="Close"
            onClick={onClose}
          />
        </div>
      </div>
      <Divider />
      <div className="decideScrollbar min-h-0 flex-1 overflow-auto">
        {renderBody()}
      </div>
    </div>
  );
};
